import { z } from 'zod';
import { buchungsseiteSchema } from '../datev/zerlegung.js';
import { isoDate, positiveAmount, uuid } from '../posting.js';
/**
 * Die Verträge des Sachkonto-Saldenvortrags — Auftrag `K-saldenvortrag`
 * (2026-10-06), Migration `0285`, `D-53` der Spec Systemeinstellungen,
 * `E-1780`.
 *
 * **Eine eigene Datei neben `geldkonto-anfangsbestand.ts`**, aus demselben
 * Grund wie jene neben `kontierung.ts`: Der Vorgang — ein Vortrag zum
 * Journalbeginn, angelegt und von einer zweiten Person freigegeben — hat mit
 * Kontierungspositionen nichts gemein. Anders als dort gibt es hier einen
 * **Schreibvertrag** für die Anlage: Die Zahl wird nicht abgeleitet, sondern
 * von einem Menschen mit Quelle erfasst (`D-53`).
 *
 * **Stichtag und Buchungsdatum stehen in keinem Anfragevertrag.** Sie werden
 * aus dem Journalbeginn (`einrichtung.journalbeginn`) ABGELEITET; ein Feld
 * dafür wäre die Stelle, an der jemand einen Vortrag ins Vorjahr oder in den
 * September legt.
 */
/** Die Richtung, in der das SACHKONTO steht. `9000` nimmt die andere Seite. */
export const sachkontoSaldenvortragRichtungSchema = buchungsseiteSchema;
/** Der Zustand einer Zeile — `sachkonto_saldenvortrag_status_bekannt` (Migration `0285`). */
export const sachkontoSaldenvortragStatusSchema = z.enum(['angelegt', 'freigegeben']);
/**
 * Die Art eines zulässigen Kontos — die drei Bilanzarten aus `account_type`
 * (`0001`). Erfolgskonten (`revenue`, `expense`) sind ausgeschlossen
 * (`sachkonto_saldenvortrag_ausschluss`, `0285`).
 */
export const sachkontoSaldenvortragKontoartSchema = z.enum(['asset', 'liability', 'equity']);
/** Eine Kontonummer — dieselbe Form wie `postingLineSchema.accountNumber`. */
const kontonummer = z.string().regex(/^[0-9]{4,6}$/, 'Kontonummer besteht aus vier bis sechs Ziffern');
const begruendung = z.string().trim().min(1, 'Begründung darf nicht leer sein').max(2000);
const quelle = z.string().trim().min(1, 'Quelle darf nicht leer sein').max(500);
/**
 * `POST /api/v1/kontierungen/saldenvortraege` — die Anlage.
 *
 * `betrag` ist echt positiv (`sachkonto_saldenvortrag_betrag_positiv`), die
 * Richtung trägt das Vorzeichen.
 */
export const sachkontoSaldenvortragAnlageSchema = z
    .object({
    kontonummer,
    richtung: sachkontoSaldenvortragRichtungSchema,
    betrag: positiveAmount,
    begruendung,
    quelle,
})
    .strict();
/**
 * `POST /api/v1/kontierungen/saldenvortraege/:id/ersetzen` — die Ersetzung
 * einer noch nicht freigegebenen Anlage, oder einer freigegebenen, deren
 * Buchung im Journal storniert ist (`D-54`, `K-saldenvortragstorno`).
 * **Ohne Kontonummer:** Die Kette wechselt das Konto nicht
 * (`sachkonto_saldenvortrag_kette`, `0285`).
 */
export const sachkontoSaldenvortragErsetzenSchema = z
    .object({
    richtung: sachkontoSaldenvortragRichtungSchema,
    betrag: positiveAmount,
    begruendung,
    quelle,
})
    .strict();
/**
 * Ein Eintrag der Liste — je Konto die JÜNGSTE Zeile seiner Kette.
 *
 * `id` ist die Kennung dieser jüngsten Zeile und damit der Wert, den Freigabe
 * und Ersetzung im Pfad erwarten — die Freigabe, solange `status` `angelegt`
 * ist, die Ersetzung, solange `ersetzbar` gilt.
 * `vorgangId` ist die Kennung der Wurzel und bleibt über die Kette gleich.
 */
export const sachkontoSaldenvortragZeileSchema = z
    .object({
    id: uuid,
    vorgangId: uuid,
    kontonummer,
    kontoname: z.string(),
    gegenkonto: kontonummer,
    stichtag: isoDate,
    buchungsdatum: isoDate,
    richtung: sachkontoSaldenvortragRichtungSchema,
    betrag: positiveAmount,
    begruendung: z.string(),
    quelle: z.string(),
    status: sachkontoSaldenvortragStatusSchema,
    createdBy: uuid,
    createdAt: z.string(),
    journalEntryId: uuid.nullable(),
    freigegebenVon: uuid.nullable(),
    freigegebenAm: z.string().nullable(),
    einzelfreigabe: z.boolean(),
    /**
     * `true`, wenn die Buchung dieser freigegebenen Zeile im Journal aufgehoben
     * ist — GELESEN aus `journal_entry.reverses_entry_id`
     * (`sachkonto_saldenvortrag_storniert()`, `0285`), keine Spalte der
     * Vortragstabelle. Für eine angelegte Zeile immer `false`
     * (`K-saldenvortragstorno`, `D-54`).
     */
    storniert: z.boolean(),
    /**
     * `true`, wenn `POST …/:id/ersetzen` diese Zeile annimmt: eine angelegte
     * Zeile, oder eine freigegebene, deren Buchung storniert ist (`D-54`).
     */
    ersetzbar: z.boolean(),
})
    .strict();
/** Ein Konto, auf das ein Saldenvortrag angelegt werden darf. */
export const sachkontoSaldenvortragKontoSchema = z
    .object({
    kontonummer,
    name: z.string(),
    art: sachkontoSaldenvortragKontoartSchema,
})
    .strict();
/**
 * `GET /api/v1/kontierungen/saldenvortraege` — die volle Antwort.
 *
 * **`konten` gehört hierher und nicht an einen zweiten Endpunkt.** Die
 * Auswahl der Maske ist dieselbe Regel wie die Abweisung beim Schreiben
 * (`sachkonto_saldenvortrag_ausschluss`, `0285`); ein zweiter Endpunkt wäre
 * eine zweite Stelle, an der sie gelesen wird. Ein Konto, das schon einen
 * Vortrag trägt, steht weiter in `konten` — die Maske blendet es aus, der
 * Dienst weist es ab.
 *
 * `journalbeginn` ist `null`, solange die Einrichtung ihn nicht gesetzt hat;
 * dann lässt sich nichts anlegen (`SALDENVORTRAG_OHNE_JOURNALBEGINN`).
 */
export const sachkontoSaldenvortragListeSchema = z
    .object({
    journalbeginn: isoDate.nullable(),
    saldenvortraege: z.array(sachkontoSaldenvortragZeileSchema),
    konten: z.array(sachkontoSaldenvortragKontoSchema),
})
    .strict();
/** `POST …/saldenvortraege/:id/freigabe` — die Antwort: neue Zeile und Buchung. */
export const sachkontoSaldenvortragFreigabeAntwortSchema = z
    .object({
    id: uuid,
    journalEntryId: uuid,
})
    .strict();
/** `POST …/saldenvortraege` und `POST …/:id/ersetzen` — die Antwort: die neue Zeile. */
export const sachkontoSaldenvortragAnlageAntwortSchema = z
    .object({
    id: uuid,
    vorgangId: uuid,
})
    .strict();
//# sourceMappingURL=sachkonto-saldenvortrag.js.map